import React, { type JSX, useState, useSyncExternalStore } from 'react';
import { Alert } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';

/**
 * Generic function type used for setting different types of "messages"
 * (applies to errors, warnings, and infos). Messages are
 * displayed in the `<AlertUI>` component returned from the
 * `useErrorsAndWarnings()` hook.
 *
 * @param message The message to set (string or JSX)
 * @param timeout Optional # of ms to remove the message
 * @returns A unique ID for the message (which can be used for
 * the `remove*Message()` functions)
 */
export type SetMessageFunction = (message: string | JSX.Element, timeout?: number) => string;

/**
 * Generic function type used for removing different types of
 * "messages" (errors, warnings, and info) from the `<AlertUI>`
 * component.
 *
 * @param id The ID for the message to be removed.
 */
export type RemoveMessageFunction = (id: string) => void;

interface AlertUIProps {
  className?: string;
}

export interface ErrorsAndWarningsResponse {
  /**
   * React component to be included in parent for displaying messages
   */
  AlertUI: React.FC<AlertUIProps>;

  /**
   * Add an error message to the UI
   */
  addErrorMessage: SetMessageFunction;

  /**
   * Add a warning message to the UI
   */
  addWarningMessage: SetMessageFunction;

  /**
   * Remove an error message from the UI (by ID)
   */
  removeErrorMessage: RemoveMessageFunction;

  /**
   * Remove a warning message from the UI (by ID)
   */
  removeWarningMessage: RemoveMessageFunction;

  /**
   * Add an informational message to the UI
   */
  addInfoMessage: SetMessageFunction;

  /**
   * Remove an informational message from the UI
   */
  removeInfoMessage: RemoveMessageFunction;
}

type MessageKind = 'errors' | 'warnings' | 'infos';
type Messages = Record<MessageKind, JSX.Element[]>;

/**
 * Creates the per-hook-instance message store, along with the `AlertUI`
 * component and add/remove functions bound to it. Built once per hook
 * instance (not per render) so `AlertUI` keeps a stable component identity:
 * a component defined fresh each render is a new type to React, which
 * unmounts and remounts it on every parent render - losing clicks on
 * buttons inside messages (e.g. an "Undo") whenever the parent re-renders
 * between pointer down and up.
 */
const createMessageStore = (): ErrorsAndWarningsResponse => {
  let messages: Messages = { errors: [], warnings: [], infos: [] };
  const listeners = new Set<() => void>();

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };
  const getSnapshot = () => messages;

  const setMessages = (kind: MessageKind, update: (prev: JSX.Element[]) => JSX.Element[]) => {
    messages = { ...messages, [kind]: update(messages[kind]) };
    listeners.forEach((listener) => listener());
  };

  const remover =
    (kind: MessageKind): RemoveMessageFunction =>
    (id) =>
      setMessages(kind, (prev) => prev.filter((item) => item.key !== id));

  const adder =
    (kind: MessageKind): SetMessageFunction =>
    (message, timeout) => {
      const uuid = uuidv4();
      const msg: JSX.Element = <div key={uuid}>{message}</div>;

      setMessages(kind, (prev) => [...prev, msg]);

      if (timeout) {
        setTimeout(() => remover(kind)(uuid), timeout);
      }

      return uuid;
    };

  const AlertUI = ({ className }: AlertUIProps) => {
    const { errors, warnings, infos } = useSyncExternalStore(subscribe, getSnapshot);

    return (
      <div className={className}>
        <Alert variant="danger" dismissible show={errors.length > 0} onClose={() => setMessages('errors', () => [])}>
          {errors}
        </Alert>
        <Alert
          variant="warning"
          dismissible
          show={warnings.length > 0}
          onClose={() => setMessages('warnings', () => [])}
        >
          {warnings}
        </Alert>
        <Alert variant="secondary" dismissible show={infos.length > 0} onClose={() => setMessages('infos', () => [])}>
          {infos}
        </Alert>
      </div>
    );
  };

  return {
    AlertUI,
    addErrorMessage: adder('errors'),
    addWarningMessage: adder('warnings'),
    addInfoMessage: adder('infos'),
    removeErrorMessage: remover('errors'),
    removeWarningMessage: remover('warnings'),
    removeInfoMessage: remover('infos'),
  };
};

/**
 * Custom hook for working with error/warning messages. Provides UI for showing
 * dismissible errors and warnings, and functions for adding same.
 *
 * Up to calling component to:
 *
 * 1. Display the `AlertUI` component in a convenient place
 * 1. Distinguish between an 'error' and a 'warning'
 * 1. Keep track of IDs from added errors/warnings, to be able to remove them
 *   (if desired)
 *
 * The returned component and functions are stable for the lifetime of the
 * calling component.
 *
 * @returns The Alert UI to be embedded, functions for adding error
 * and warning messages; and functions for removing error and warning
 * messages
 */
export const useErrorsAndWarnings = (): ErrorsAndWarningsResponse => {
  const [store] = useState(createMessageStore);
  return store;
};
